import { computed, reactive } from "vue";

type Status = "idle" | "loading" | "ready" | "unavailable";
interface Session {
  version: 1;
  accountKey: string;
  loginId: string;
  status: Exclude<Status, "loading">;
  mfaEnabled: boolean | null;
}

const storageKey = "vd.userCatalogSettings.v1";
const state = reactive({
  loginId: null as string | null,
  status: "idle" as Status,
  mfaEnabled: null as boolean | null,
  failed: false,
});
let session: Session | null = null;
let pending: Promise<void> | null = null;

function persist() {
  try {
    if (session) {
      window.sessionStorage.setItem(storageKey, JSON.stringify(session));
    } else {
      window.sessionStorage.removeItem(storageKey);
    }
  } catch {
    // Storage may be unavailable; retain the current page's in-memory session.
  }
}

function clearSession() {
  session = null;
  pending = null;
  state.loginId = null;
  state.status = "idle";
  state.mfaEnabled = null;
  state.failed = false;
  persist();
}

function beginSession(accountKey: string) {
  if (session?.accountKey === accountKey) return;

  let saved: Session | null = null;
  try {
    const value = JSON.parse(
      window.sessionStorage.getItem(storageKey) || "null"
    );
    if (
      value?.version === 1 &&
      value.accountKey === accountKey &&
      typeof value.loginId === "string" &&
      value.loginId.length > 0 &&
      ((value.status === "ready" && typeof value.mfaEnabled === "boolean") ||
        ((value.status === "idle" || value.status === "unavailable") &&
          value.mfaEnabled === null))
    ) {
      saved = value;
    }
  } catch {
    // Invalid cached data is replaced with a fresh lookup.
  }

  session = saved || {
    version: 1,
    accountKey,
    loginId: Array.from(window.crypto.getRandomValues(new Uint32Array(4))).join(
      "-"
    ),
    status: "idle",
    mfaEnabled: null,
  };
  pending = null;
  state.loginId = session.loginId;
  state.status = session.status;
  state.mfaEnabled = session.mfaEnabled;
  state.failed = session.status === "unavailable";
  persist();
}

function load(fetchSetting: () => Promise<boolean>, retry = false) {
  if (!session || state.status === "ready") return Promise.resolve();
  if (pending) return pending;
  if (state.status === "unavailable" && !retry) return Promise.resolve();

  const activeSession = session;
  state.status = "loading";
  pending = (async () => {
    try {
      const mfaEnabled = await Promise.resolve().then(fetchSetting);
      if (typeof mfaEnabled !== "boolean")
        throw new Error("Invalid MFA setting");
      if (session !== activeSession) return;

      state.mfaEnabled = mfaEnabled;
      state.status = "ready";
      state.failed = false;
      activeSession.status = "ready";
      activeSession.mfaEnabled = mfaEnabled;
    } catch {
      if (session !== activeSession) return;
      state.mfaEnabled = null;
      state.status = "unavailable";
      state.failed = true;
      activeSession.status = "unavailable";
      activeSession.mfaEnabled = null;
    } finally {
      if (session === activeSession) {
        pending = null;
        persist();
      }
    }
  })();
  return pending;
}

export default function useUserCatalogSettings() {
  return {
    sessionId: computed(() => state.loginId),
    status: computed(() => state.status),
    failed: computed(() => state.failed),
    mfaEnabled: computed(
      () => state.status === "ready" && state.mfaEnabled === true
    ),
    beginSession,
    clearSession,
    load,
  };
}
